import {
  Autocomplete,
  createFilterOptions,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { formatReportedAt } from "../../utils/taskSheetDate";
import { STATUS_COLORS } from "../../utils/issueStatus";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Webcam from "react-webcam";
import { handleToastMsg } from "../../components/Toast";
import CloseIcon from "@mui/icons-material/Close";
import LinkedCameraIcon from "@mui/icons-material/LinkedCamera";
import { DEPARTMENTS_TASKSHEETS, HALL_NO } from "../../config/masterdata";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  createIssueReason,
  createTaskSheetsIssueData,
  getReasonPermissions,
  getActiveEmployees,
  getIssueReasons,
  getOpenMachines,
  updateTaskSheetsIssueData,
} from "./mutation";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import { FileUpload, PlayCircleOutline, StopCircle } from "@mui/icons-material";
import {
  MAX_TASK_SHEET_IMAGES,
  solutionImages,
} from "../../utils/solutionImages";

const filterReasons = createFilterOptions();
// Reason is required on new reports; keep in step with the backend's
// reportIssueForm.REASON_REQUIRED
const REASON_REQUIRED = true;
const normalizeReason = (value) => String(value ?? "").trim().replace(/\s+/g, " ");

// The server's reason for a failed save ("Failed to update issue: ...",
// "Employee name is required to resolve"), else a generic message
const errorMessage = (error) => {
  const data = error?.response?.data;
  const reason = [data?.message, data?.error].filter(Boolean).join(": ");
  return reason || "Data Error Occurred";
};

function TaskSheetReportAndResolve({
  isManager,
  isSeek,
  isResolve,
  goBack,
  isRaiseReport,
  showIssueData,
  filteredManagers,
  isTelevision,
}) {
  const queryClient = useQueryClient();

  const [selectedDate, setSelectedDate] = useState(null);
  const today = dayjs();

  //this is for manager resolve
  const [solution, setSolution] = useState("");
  const [seekReason, setSeekReason] = useState("");
  const handleGoBack = () => {
    goBack();
  };

  // this all for raise an report
  const [departments, setDepartments] = useState([]);
  const [reportingManager, setReportingManager] = useState([]);
  const [hallNo, setHallNo] = useState("");

  // Employee the issue is about: picked by the manager when resolving
  // (required); pre-filled when the issue already has one
  const [employee, setEmployee] = useState(
    showIssueData?.employeeNo
      ? {
          emplNo: showIssueData.employeeNo,
          name: showIssueData.employeeName,
        }
      : null
  );
  const {
    data: employees = [],
    isLoading: isEmployeesLoading,
    isError: isEmployeesError,
  } = useQuery(
    "erp-employees",
    getActiveEmployees,
    {
      enabled: Boolean(isResolve) && !isTelevision,
      staleTime: 1000 * 60 * 30,
    }
  );
  const [issue, setIssue] = useState("");
  // Optional "Emergency" tick on the report form
  const [isEmergency, setIsEmergency] = useState(false);

  // Required machine (ERP machine list, synced daily)
  const [machine, setMachine] = useState(null);
  const {
    data: machines = [],
    isLoading: isMachinesLoading,
    isError: isMachinesError,
  } = useQuery("erp-machines", getOpenMachines, {
    enabled: Boolean(isRaiseReport),
    staleTime: 1000 * 60 * 30,
  });

  // Reason (required, see REASON_REQUIRED), picked from the list; only the
  // logins in the backend's REASON_EDITOR_EMPLOYEE_CODES may add new ones
  const [reason, setReason] = useState(null);
  const { data: reasons = [], isLoading: isReasonsLoading } = useQuery(
    "issue-reasons",
    getIssueReasons,
    { enabled: Boolean(isRaiseReport), staleTime: 1000 * 60 * 5 }
  );
  const { data: canAddReasons = false } = useQuery(
    ["issue-reason-permissions", localStorage.getItem("id")],
    getReasonPermissions,
    { enabled: Boolean(isRaiseReport), staleTime: 1000 * 60 * 5 }
  );
  const { mutate: addReason, isLoading: isAddingReason } =
    useMutation(createIssueReason);

  const handleReasonChange = (event, value) => {
    if (!value?.isNew) {
      setReason(value);
      return;
    }
    addReason(value.inputValue, {
      onSuccess: (added) => {
        setReason(added);
        // Reload the list in the server's order; the server returns the
        // existing reason if the name was already there
        queryClient.invalidateQueries("issue-reasons");
        handleToastMsg(`Reason "${added.name}" added`, "success");
      },
      onError: (error) => {
        handleToastMsg(
          error?.response?.data?.message || "Unable to add the reason",
          "error"
        );
      },
    });
  };
  const [openDialog, setOpenDialog] = useState(false);
  // Manager resolve images (up to MAX_TASK_SHEET_IMAGES); selectedFile is the
  // first, which the required-image checks look at
  const [selectedFiles, setSelectedFiles] = useState([]);
  const selectedFile = selectedFiles[0] ?? null;
  const [selectedFilesMulti, setSelectedFilesMulti] = useState([]);
  const [openCamera, setOpenCamera] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");

  // video content start  ---start----------------------------------------------------------------------------------------

  const [openDialogVideo, setOpenDialogVideo] = useState(false);
  const [openVideoCamera, setOpenVideoCamera] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  // const [videoBlob, setVideoBlob] = useState(null);
  const [videoRecordUrl, setVideoRecordUrl] = useState(null);
  const [videoRecordFile, setVideoRecordFile] = useState(null);
  // const [videoBlobUrl, setVideoBlobUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0); // in seconds
  const [recordingInterval, setRecordingInterval] = useState(null);
  const [recordingTimeout, setRecordingTimeout] = useState(null);
  // const [urlFile, setUrlFile] = useState(null);
  const [, setUrlFile] = useState(null);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const [loadingVideo, setLoadingVideo] = useState(false);

  const handleCloseDialogVideo = () => {
    setOpenDialogVideo(false);
    setOpenVideoCamera(false);
    setIsRecording(false);
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    clearInterval(recordingInterval);
    setSelectedVideo(null);
    setUrlFile(null);
    setVideoRecordUrl(null);
    setVideoRecordFile(null);
    setLoadingVideo(false);
  };

  // video upload file
  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file.type !== "video/mp4") {
      alert("Please select a valid MP4 video file.");
      return;
    }
    if (file && file.size <= 30485760) {
      const videoElement = document.createElement("video");
      const fileUrl = URL.createObjectURL(file); // Blob URL for original video
      videoElement.src = fileUrl;
      videoElement.muted = true; // Mute the video during processing

      videoElement.onloadedmetadata = () => {
        // Check video duration
        if (videoElement.duration <= 31) {
          compressVideo(videoElement, fileUrl);
        } else {
          alert("Please select a video file shorter than 31 seconds.");
          URL.revokeObjectURL(fileUrl); // Clean up Blob URL to avoid memory leaks
        }
      };
      videoElement.onerror = () => {
        alert(
          "Error loading the video. Please ensure it is a valid video file and not corrupted."
        );
        URL.revokeObjectURL(fileUrl); // Clean up Blob URL
      };
    } else {
      alert("Please select a video file less than 30MB.");
    }
  };
  // const compressVideo = (videoElement, fileUrl) => {
  //   if (!selectedVideo) {
  //     setLoadingVideo(true);
  //   }
  //   const canvas = document.createElement("canvas");
  //   const ctx = canvas.getContext("2d");
  //   const outputWidth = 320; // e.g., reduce the resolution to 320px width
  //   const outputHeight =
  //     (videoElement.videoHeight / videoElement.videoWidth) * outputWidth;

  //   canvas.width = outputWidth;
  //   canvas.height = outputHeight;
  //   const stream = canvas.captureStream();
  //   const mediaRecorder = new MediaRecorder(stream, {
  //     mimeType: "video/webm; codecs=vp9", // WebM format is widely supported
  //   });

  //   const chunks = [];
  //   let isRecording = false; // Flag to ensure recording happens only once

  //   mediaRecorder.ondataavailable = (event) => {
  //     if (event.data.size > 0) {
  //       chunks.push(event.data);
  //     }
  //   };

  //   mediaRecorder.onstop = () => {
  //     if (!isRecording) return; // Prevent multiple executions
  //     isRecording = false; // Reset the flag after recording is done

  //     const compressedBlob = new Blob(chunks, { type: "video/webm" });
  //     const base64Reader = new FileReader();

  //     // Convert Blob to Base64
  //     base64Reader.onloadend = () => {
  //       const base64Url = base64Reader.result;
  //       if (typeof base64Url === "string") {
  //         // Store the Base64 URL for backend submission
  //         setUrlFile(base64Url);
  //         // Store Blob URL for video preview in the frontend (original video)
  //         setSelectedVideo(fileUrl); // Set original video Blob URL
  //         // Close dialog after valid selection
  //         setOpenDialogVideo(false);
  //         setLoadingVideo(false);
  //       } else {
  //         alert("Error converting the video to Base64 format.");
  //       }
  //     };

  //     base64Reader.readAsDataURL(compressedBlob); // Read the compressed video as Base64
  //     URL.revokeObjectURL(fileUrl); // Clean up Blob URL to avoid memory leaks
  //   };

  //   // Start recording and draw the video frames onto the canvas
  //   mediaRecorder.start();
  //   isRecording = true;

  //   const drawFrame = () => {
  //     if (!isRecording) return; // Exit if recording has stopped
  //     ctx.drawImage(videoElement, 0, 0, outputWidth, outputHeight);

  //     if (!videoElement.paused && !videoElement.ended) {
  //       requestAnimationFrame(drawFrame);
  //     } else {
  //       mediaRecorder.stop();
  //     }
  //   };

  //   // Start playing the video and begin drawing frames
  //   videoElement.play();
  //   drawFrame();
  // };

  //video base4 url and blob

  // const convertVideoToBase64AndBlob = (videoElement, fileUrl) => {
  //   if (!selectedVideo) {
  //     setLoadingVideo(true);
  //   }

  //   const base64Reader = new FileReader();

  //   // Convert the video file to Base64
  //   base64Reader.onloadend = () => {
  //     const base64Url = base64Reader.result;
  //     if (typeof base64Url === "string") {
  //       // Store the Base64 URL for backend submission
  //       setUrlFile(base64Url);
  //       // Store Blob URL for video preview in the frontend (original video)
  //       setSelectedVideo(fileUrl); // Set original video Blob URL
  //       // Close dialog after valid selection
  //       setOpenDialogVideo(false);
  //       setLoadingVideo(false);
  //     } else {
  //       alert("Error converting the video to Base64 format.");
  //     }
  //   };

  //   // Read the video file as Base64
  //   fetch(fileUrl)
  //     .then((res) => res.blob())
  //     .then((blob) => {
  //       base64Reader.readAsDataURL(blob);
  //     })
  //     .catch(() => {
  //       alert("Error reading the video file.");
  //       setLoadingVideo(false);
  //     });
  // };

  const compressVideo = (videoElement, fileUrl) => {
    //compressvideo but is hight quality
    if (!selectedVideo) {
      setLoadingVideo(true);
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Increase the resolution for better quality
    const outputWidth = 640; // Adjust to 640px or higher
    const outputHeight =
      (videoElement.videoHeight / videoElement.videoWidth) * outputWidth;

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const stream = canvas.captureStream();
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: "video/webm; codecs=vp9",
      videoBitsPerSecond: 2500000, // Increase bitrate (2.5 Mbps for better quality)
    });

    const chunks = [];
    let isRecording = false; // Flag to ensure recording happens only once

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      if (!isRecording) return; // Prevent multiple executions
      isRecording = false; // Reset the flag after recording is done

      const compressedBlob = new Blob(chunks, { type: "video/webm" });
      const base64Reader = new FileReader();

      // Convert Blob to Base64
      base64Reader.onloadend = () => {
        const base64Url = base64Reader.result;
        if (typeof base64Url === "string") {
          // Store the Base64 URL for backend submission
          setUrlFile(base64Url);
          // Store Blob URL for video preview in the frontend (original video)
          setSelectedVideo(fileUrl); // Set original video Blob URL
          // Close dialog after valid selection
          setOpenDialogVideo(false);
          setLoadingVideo(false);
        } else {
          alert("Error converting the video to Base64 format.");
        }
      };

      base64Reader.readAsDataURL(compressedBlob); // Read the compressed video as Base64
      URL.revokeObjectURL(fileUrl); // Clean up Blob URL to avoid memory leaks
    };

    // Start recording and draw the video frames onto the canvas
    mediaRecorder.start();
    isRecording = true;

    const drawFrame = () => {
      if (!isRecording) return; // Exit if recording has stopped
      ctx.drawImage(videoElement, 0, 0, outputWidth, outputHeight);

      if (!videoElement.paused && !videoElement.ended) {
        requestAnimationFrame(drawFrame);
      } else {
        mediaRecorder.stop();
      }
    };

    // Start playing the video and begin drawing frames
    videoElement.play();
    drawFrame();
  };
  //open camera
  const handleCameraVideoClick = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setOpenVideoCamera(true);
    } catch (error) {
      console.error("Error accessing webcam: ", error);
    }
  };

  //video recording part
  const handleStartStopRecording = () => {
    if (isRecording) {
      // Stop recording
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingInterval); // Clear the timer
      clearTimeout(recordingTimeout); // Clear the timeout for automatic stopping
    } else {
      // Start recording
      const mediaRecorder = new MediaRecorder(mediaStreamRef.current, {
        mimeType: "video/x-matroska", // Change to MKV format
      });
      mediaRecorderRef.current = mediaRecorder;

      const chunks = []; // Array to hold recorded data chunks

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data); // Push data chunks into the array
        }
      };

      mediaRecorder.onstop = () => {
        // Create a blob from the recorded chunks
        const blob = new Blob(chunks, { type: "video/x-matroska" });
        const fileUrl = URL.createObjectURL(blob); // Create a URL for the recorded video

        // Set videoBlob for preview
        // setVideoBlob(fileUrl);

        // Create a video element to play the recorded video
        const videoElement = document.createElement("video");
        videoElement.src = fileUrl;
        videoElement.onloadedmetadata = () => {
          convertVideoToUrls(videoElement, fileUrl); // Compress the video after it is loaded
        };

        // Convert Blob URL to Base64 for storage
        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          // const base64Url = fileReader.result; // Base64 encoded string
          // setVideoBlobUrl(base64Url); // Store the Base64 URL for use
        };
        fileReader.readAsDataURL(blob); // Read Blob data as Base64
      };

      mediaRecorder.onerror = (e) => {
        console.error("MediaRecorder error: ", e);
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Start recording timer
      const startTime = Date.now();
      const interval = setInterval(() => {
        setRecordingTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      setRecordingInterval(interval);

      // Automatically stop recording after 30 seconds
      const timeout = setTimeout(() => {
        mediaRecorderRef.current.stop();
        setIsRecording(false);

        clearInterval(recordingInterval);
      }, 30000); // 30 seconds limit
      setRecordingTimeout(timeout);
    }
  };

  //base64 url and blob url converter

  // const convertVideoToUrls = (videoElement, fileUrl) => {
  //   // Show loading indicator
  //   setLoadingVideo(true);

  //   const base64Reader = new FileReader();

  //   // Create a new canvas
  //   const canvas = document.createElement("canvas");
  //   const ctx = canvas.getContext("2d");

  //   // Set the canvas dimensions to match the video
  //   const outputWidth = videoElement.videoWidth;
  //   const outputHeight = videoElement.videoHeight;

  //   canvas.width = outputWidth;
  //   canvas.height = outputHeight;

  //   // Create an audio context
  //   const audioContext = new (window.AudioContext ||
  //     window.webkitAudioContext)();
  //   const source = audioContext.createMediaElementSource(videoElement);
  //   const gainNode = audioContext.createGain();

  //   // Set the gain to 0 to mute
  //   gainNode.gain.setValueAtTime(0, audioContext.currentTime);
  //   source.connect(gainNode);
  //   gainNode.connect(audioContext.destination); // Connect to speakers

  //   // Create a MediaStream that includes both video and audio tracks
  //   const stream = videoElement.captureStream(); // Captures video and audio from the video element
  //   const mediaRecorder = new MediaRecorder(stream, {
  //     mimeType: "video/webm; codecs=vp9,opus", // Include audio codec
  //   });

  //   const chunks = [];

  //   mediaRecorder.ondataavailable = (event) => {
  //     if (event.data.size > 0) {
  //       chunks.push(event.data);
  //     }
  //   };

  //   mediaRecorder.onstop = () => {
  //     // Create Blob from recorded chunks
  //     const blob = new Blob(chunks, { type: "video/webm" });

  //     // Create Blob URL for preview
  //     const blobUrl = URL.createObjectURL(blob);
  //     setVideoRecordFile(blobUrl); // Set original video Blob URL

  //     // Convert Blob to Base64
  //     base64Reader.onloadend = () => {
  //       const base64Url = base64Reader.result;
  //       if (typeof base64Url === "string") {
  //         // Store the Base64 URL for backend submission
  //         setVideoRecordUrl(base64Url);
  //       } else {
  //         alert("Error converting the video to Base64 format.");
  //       }
  //       setLoadingVideo(false); // Hide loading indicator
  //     };

  //     base64Reader.readAsDataURL(blob); // Read the Blob as Base64
  //     URL.revokeObjectURL(fileUrl); // Clean up Blob URL to avoid memory leaks
  //   };

  //   // Start recording
  //   mediaRecorder.start();

  //   const drawFrame = () => {
  //     // Draw the current video frame onto the canvas
  //     ctx.drawImage(videoElement, 0, 0, outputWidth, outputHeight);

  //     if (!videoElement.paused && !videoElement.ended) {
  //       requestAnimationFrame(drawFrame); // Continue drawing frames
  //     } else {
  //       mediaRecorder.stop(); // Stop recording when the video ends
  //     }
  //   };

  //   videoElement.play(); // Start playing the video
  //   drawFrame(); // Start drawing frames
  // };

  // Increase bitrate for better quality (1.5 Mbps)
  const convertVideoToUrls = (videoElement, fileUrl) => {
    if (!selectedVideo) {
      setLoadingVideo(true);
    }
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Increase resolution for better quality
    const outputWidth = 640; // Higher resolution (e.g., 640px width)
    const outputHeight =
      (videoElement.videoHeight / videoElement.videoWidth) * outputWidth;

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const stream = canvas.captureStream(24); // Use 24 fps for smoother motion
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: "video/webm; codecs=vp9",
      videoBitsPerSecond: 2500000, // Increase bitrate for better quality (1.5 Mbps)
    });

    const chunks = [];
    let isRecording = false;

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      if (!isRecording) return;
      isRecording = false;

      const compressedBlob = new Blob(chunks, { type: "video/webm" });
      const base64Reader = new FileReader();

      // Convert Blob to Base64
      base64Reader.onloadend = () => {
        const base64Url = base64Reader.result;
        if (typeof base64Url === "string") {
          // Store the Base64 URL for backend submission
          setVideoRecordUrl(base64Url);
          setVideoRecordFile(fileUrl);
        } else {
          alert("Error converting the video to Base64 format.");
        }
        setLoadingVideo(false); // Hide loading indicator
      };

      base64Reader.readAsDataURL(compressedBlob);
      URL.revokeObjectURL(fileUrl);
    };

    mediaRecorder.start();
    isRecording = true;

    const drawFrame = () => {
      if (!isRecording) return;
      ctx.drawImage(videoElement, 0, 0, outputWidth, outputHeight);

      if (!videoElement.paused && !videoElement.ended) {
        setTimeout(() => requestAnimationFrame(drawFrame), 1000 / 24); // 24 fps limit for smoother motion
      } else {
        mediaRecorder.stop();
      }
    };

    videoElement.play();
    videoElement.muted = true;
    drawFrame();
  };

  //video timing function
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  //video capture
  const captureVideo = () => {
    handleCloseDialogVideo();
    setSelectedVideo(videoRecordFile);
    setOpenVideoCamera(false);
    setUrlFile(videoRecordUrl);
    setSelectedVideo(videoRecordFile);
  };

  useEffect(() => {
    return () => clearInterval(recordingInterval); // Clean up on unmount
  }, [recordingInterval]);

  //video end --end---------------------------------------------------------------------------------------------------

  const handleToggleCamera = () => {
    setFacingMode((prevFacingMode) =>
      prevFacingMode === "user" ? "environment" : "user"
    );
  };

  const handleManagerChange = (event) => {
    const { value } = event.target;

    const selectedIds = typeof value === "string" ? value.split(",") : value;

    const selectedManagers = selectedIds
      ?.map((id) => {
        const manager = filteredManagers?.find((mgr) => mgr.id === id);
        return manager ? { id: manager.id, name: manager.name } : null;
      })
      .filter(Boolean);

    setReportingManager(selectedManagers);
  };

  const toggleManagerSelection = (manager) => {
    setReportingManager((prev) => {
      const isSelected = prev.some((m) => m.id === manager.id);
      if (isSelected) {
        return prev.filter((m) => m.id !== manager.id);
      }
      return [...prev, { id: manager.id, name: manager.name }];
    });
  };

  const webcamRef = useRef(null);

  const webcamRefVideo = useRef(null);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setOpenCamera(false);
  };

  // const handleOpenDialogVideo = () => {
  //   setOpenDialogVideo(true);
  // };

  // Reads an image as a data URL; anything over 1MB is scaled to at most
  // 1000px and re-encoded until it fits
  const readImage = (file, onReady) => {
    const reader = new FileReader();
    if (file.size <= 1048576) {
      reader.onloadend = () => onReady(reader.result);
      reader.readAsDataURL(file);
      return;
    }

    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxWidth = 1000;
        const maxHeight = 1000;
        let width = img.width;
        let height = img.height;

        if (width > height && width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        } else if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);

        let quality = 1.0;
        let compressedDataUrl = canvas.toDataURL(file.type, quality);
        while (compressedDataUrl.length > 1048576 && quality > 0.3) {
          quality -= 0.02;
          compressedDataUrl = canvas.toDataURL(file.type, quality);
        }
        onReady(compressedDataUrl);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Manager resolving: solution images; employee reporting: issue images.
  // Either way at most MAX_TASK_SHEET_IMAGES.
  const isResolveImages = isResolve && !isTelevision;
  const addImage = (dataUrl) => {
    const append = (prev) =>
      prev.length >= MAX_TASK_SHEET_IMAGES ? prev : [...prev, dataUrl];
    if (isResolveImages) setSelectedFiles(append);
    else if (isRaiseReport) setSelectedFilesMulti(append);
  };

  //handle image select for single and multiple(MANAGER AND EMPLOYEE)
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // lets the same file be picked again after removing it

    const images = files.filter(
      (file) => file.type === "image/jpeg" || file.type === "image/png"
    );
    if (images.length < files.length) {
      handleToastMsg("Please select JPG or PNG files only.", "info");
    }

    const current = isResolveImages
      ? selectedFiles.length
      : selectedFilesMulti.length;
    const room = Math.max(MAX_TASK_SHEET_IMAGES - current, 0);
    if (images.length > room) {
      handleToastMsg(
        `You can upload up to ${MAX_TASK_SHEET_IMAGES} images`,
        "info"
      );
    }

    images.slice(0, room).forEach((file) => readImage(file, addImage));
    setOpenDialog(false);
  };

  const handleCameraClick = () => {
    setOpenCamera(true);
  };

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();

    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        readImage(blob, addImage);
        setOpenCamera(false);
        setOpenDialog(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamRef]);

  const handleRemoveImage = (index) => {
    setSelectedFilesMulti((prevFiles) =>
      prevFiles.filter((_, i) => i !== index)
    );
  };

  const resetForm = () => {
    goBack();
  };

  const { mutate, isLoading: isReportCreateLoading } = useMutation(
    createTaskSheetsIssueData
  );
  const { mutate: updateReportMutation, isLoading: isReportUpdateLoading } =
    useMutation(updateTaskSheetsIssueData);
  //handle report issue submit

  const checkForAllFields =
    departments.length > 0 &&
    reportingManager.length > 0 &&
    hallNo !== "" &&
    (!REASON_REQUIRED || reason !== null) &&
    machine !== null &&
    issue !== "";

  const reportingManagerSolutions = reportingManager?.map((item) => {
    return {
      id: item.id,
      name: item.name,
      solution: null,
      solutionDate: null,
      seekedDate: null,
      seekReason: null,
      image: null,
      accepted: null,
      rejectedCount: 0,
    };
  });

  const handleSubmitReportIssue = () => {
    const payload = {
      date: today.format("YYYY-MM-DD"),
      department: departments,
      reportingManager: reportingManager,
      reportingManagerSolution: reportingManagerSolutions,
      description: issue,
      isEmergency,
      // Table reasons go by id; configured defaults (no id) by name
      reasonId: reason?.id ?? undefined,
      reasonName: reason && !reason.id ? reason.name : undefined,
      machineCode: machine?.mcCode,
      // ...(selectedFile && { image: selectedFile }),
      image: selectedFilesMulti?.length > 0 ? selectedFilesMulti : null,
      // video: urlFile ? urlFile : null,
      status: "PENDING",
      hall: hallNo,
    };

    mutate(payload, {
      onSuccess: (res) => {
        handleToastMsg("Data submitted successfully", "success");
        queryClient.invalidateQueries("task-sheets-report-issue");
        resetForm();
      },
      onError: (error) => {
        handleToastMsg(errorMessage(error), "error");
      },
    });
  };

  const managerId = parseInt(localStorage.getItem("id"), 10);

  const managerIdFilter = showIssueData?.reportingManager?.filter(
    (item) => parseInt(item.id, 10) === managerId
  );

  const otherManagerResponse =
    showIssueData?.reportingManagerSolution?.filter(
      (item) => parseInt(item.id, 10) !== managerId
    ) || [];

  // const existManagerResponse =
  //   showIssueData?.reportingManagerSolution?.filter(
  //     (item) => parseInt(item.id, 10) === managerId
  //   ) || [];

  const existManagerResponse = useMemo(() => {
    return (
      showIssueData?.reportingManagerSolution?.filter(
        (item) => parseInt(item.id, 10) === managerId
      ) || []
    );
  }, [showIssueData?.reportingManagerSolution, managerId]);

  // Resolving after a decline is a new solution: the form starts empty and the
  // declined one is shown above it (the server keeps it in the history).
  // Seek keeps the solution as it is.
  const isResubmit = isResolve && existManagerResponse[0]?.accepted === false;
  const declinedSolution = isResubmit ? existManagerResponse[0] : null;
  const declineReviews = (declinedSolution?.reviews || []).filter(
    (review) => review?.decision === "decline"
  );
  const lastDecline = declineReviews[declineReviews.length - 1];

  useEffect(() => {
    if (isResubmit) return;

    if (existManagerResponse[0]?.solution) {
      setSolution(existManagerResponse[0]?.solution);
    }

    // Earlier images of this manager (older solutions have a single image)
    const earlierImages = solutionImages(existManagerResponse[0]);
    if (earlierImages.length) {
      setSelectedFiles(earlierImages.slice(0, MAX_TASK_SHEET_IMAGES));
    }
  }, [existManagerResponse, isResubmit]);
  

  //handle update the issue reports data (seek or resolve)
  const handleSeekOrResolve = () => {
    if (isResolve && !selectedFile) {
      handleToastMsg("Please upload a resolve image", "info");
      return;
    }
    if (isResolve && !employee) {
      handleToastMsg("Please select the employee name", "info");
      return;
    }

    const params = {
      payload: {
        ...showIssueData,
        ...(isResolve && { employeeNo: employee?.emplNo }),
        ...(isSeek && {
          reportingManagerSolution:
            otherManagerResponse?.length > 0
              ? [
                  ...otherManagerResponse,
                  {
                    id:
                      managerIdFilter?.length > 0 ? managerIdFilter[0]?.id : "",
                    name:
                      managerIdFilter?.length > 0
                        ? managerIdFilter[0]?.name
                        : "",
                    solution:
                      existManagerResponse?.length > 0
                        ? existManagerResponse[0]?.solution
                        : null,
                    solutionDate:
                      existManagerResponse?.length > 0
                        ? existManagerResponse[0]?.solutionDate
                        : null,
                    seekedDate: dayjs(selectedDate).format("YYYY-MM-DD"),
                    seekReason: seekReason,
                    // image: existManagerResponse[0]?.image || selectedFile,
                    image: selectedFile,
                    images: selectedFiles,
                    accepted: existManagerResponse[0]?.accepted,
                    rejectedCount: existManagerResponse[0]?.rejectedCount,
                  },
                ]
              : [
                  {
                    id:
                      managerIdFilter?.length > 0 ? managerIdFilter[0]?.id : "",
                    name:
                      managerIdFilter?.length > 0
                        ? managerIdFilter[0]?.name
                        : "",
                    solution:
                      existManagerResponse?.length > 0
                        ? existManagerResponse[0]?.solution
                        : null,
                    solutionDate:
                      existManagerResponse?.length > 0
                        ? existManagerResponse[0]?.solutionDate
                        : null,
                    seekedDate: dayjs(selectedDate).format("YYYY-MM-DD"),
                    seekReason: seekReason,
                    // image: existManagerResponse[0]?.image || selectedFile,
                    image: selectedFile,
                    images: selectedFiles,
                    accepted: existManagerResponse[0]?.accepted,
                    rejectedCount: existManagerResponse[0]?.rejectedCount,
                  },
                ],
        }),
        ...(isResolve && {
          reportingManagerSolution:
            otherManagerResponse.length > 0
              ? [
                  ...otherManagerResponse,
                  {
                    id:
                      managerIdFilter?.length > 0 ? managerIdFilter[0]?.id : "",
                    name:
                      managerIdFilter?.length > 0
                        ? managerIdFilter[0]?.name
                        : "",
                    seekedDate:
                      existManagerResponse.length > 0
                        ? existManagerResponse[0]?.seekedDate
                        : null,
                    seekReason:
                      existManagerResponse.length > 0
                        ? existManagerResponse[0]?.seekReason
                        : null,
                    solutionDate: today.toISOString(),
                    solution: solution,
                    // image: existManagerResponse[0]?.image || selectedFile,
                    image: selectedFile,
                    images: selectedFiles,
                    accepted: null,
                    rejectedCount: existManagerResponse[0]?.rejectedCount,
                  },
                ]
              : [
                  {
                    id:
                      managerIdFilter?.length > 0 ? managerIdFilter[0]?.id : "",
                    name:
                      managerIdFilter?.length > 0
                        ? managerIdFilter[0]?.name
                        : "",
                    seekedDate:
                      existManagerResponse.length > 0
                        ? existManagerResponse[0]?.seekedDate
                        : null,
                    seekReason:
                      existManagerResponse.length > 0
                        ? existManagerResponse[0]?.seekReason
                        : null,
                    solutionDate: today.toISOString(),
                    solution: solution,
                    // image: existManagerResponse[0]?.image || selectedFile,
                    image: selectedFile,
                    images: selectedFiles,
                    accepted: null,
                    rejectedCount: existManagerResponse[0]?.rejectedCount,
                  },
                ],
        }),
      },
      id: showIssueData?.id,
    };

    updateReportMutation(params, {
      onSuccess: (res) => {
        handleToastMsg("Data updates successfully", "success");
        queryClient.invalidateQueries("task-sheets-report-issue");
        resetForm();
      },
      onError: (error) => {
        handleToastMsg(errorMessage(error), "error");
      },
    });
  };

  const [openNew, setOpenNew] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const handleClickOpen = (image) => {
    setSelectedImage(image);
    setOpenNew(true);
  };

  const handleClose = () => {
    setOpenNew(false);
  };

  //this is for checking the seeked date or not

  const isManagerResponse = showIssueData?.reportingManagerSolution?.filter(
    (item) => parseInt(item.id, 10) === managerId
  );

  //this is for checking to show the date select (DatePicker)
  const showSeekDateSelect =
    isManagerResponse?.length > 0
      ? showIssueData?.reportingManagerSolution === null ||
        isManagerResponse[0]?.seekedDate === null
      : true;

  //this is for checking to show the seeked date if there is in the data thats why we have default false
  const showSeekedDate =
    isManagerResponse?.length > 0
      ? isManagerResponse[0]?.seekedDate !== null
      : false;

  return (
    <Grid container item xs={12} alignContent={"flex-start"} rowGap={3}>
      <Grid
        container
        item
        xs={12}
        sx={{
          mt: {
            xs: "15px",
            sm: "0px",
          },
        }}
      >
        <Button
          onClick={handleGoBack}
          style={{
            color: "black",
            textTransform: "none",
            outline: "1px solid grey",
          }}
        >
          <ArrowBackIcon /> <span style={{ paddingLeft: "10px" }}>Back</span>
        </Button>
      </Grid>

      {isManager && (
        <>
          <Grid container item xs={12} rowGap={3}>
            <Grid container item xs={12} alignItems={"center"}>
              <Grid item xs={12} sm={6} md={6}>
                <span style={{ fontWeight: "600" }}>Sl No :</span>{" "}
                <span style={{ marginLeft: "5px" }}>
                  {" "}
                  {showIssueData?.slNo}
                </span>
              </Grid>
              <Grid
                sx={{
                  "@media (max-width: 600px)": {
                    marginTop: "20px",
                  },
                }}
                item
                xs={12}
                sm={6}
                md={6}
              >
                <span style={{ fontWeight: "600" }}>Department :</span>{" "}
                <span style={{ marginLeft: "5px" }}>
                  {" "}
                  {showIssueData?.department
                    ?.map((item) => item)
                    .join(", ")}{" "}
                </span>
              </Grid>
            </Grid>
            <Grid container item xs={12} alignItems={"center"}>
              {" "}
              <Grid item xs={12} sm={6} md={6}>
                <span style={{ fontWeight: "600" }}> Date :</span>
                <span style={{ marginLeft: "5px" }}>
                  {" "}
                  {formatReportedAt(showIssueData)}
                </span>
              </Grid>
              <Grid
                sx={{
                  "@media (max-width: 600px)": {
                    marginTop: "20px",
                  },
                }}
                item
                xs={12}
                sm={6}
                md={6}
              >
                <span style={{ fontWeight: "600" }}>Reported By :</span>
                <span style={{ marginLeft: "5px" }}>
                  {" "}
                  {showIssueData?.reportedBy}
                </span>
              </Grid>
            </Grid>
            {showIssueData?.employeeName && (
              <Grid container item xs={12} alignItems={"center"}>
                <Grid item xs={12} sm={6} md={6}>
                  <span style={{ fontWeight: "600" }}>Employee Name :</span>
                  <span style={{ marginLeft: "5px" }}>
                    {" "}
                    {showIssueData?.employeeName}
                  </span>
                </Grid>
              </Grid>
            )}
            {showIssueData?.isEmergency && (
              <Grid container item xs={12} alignItems={"center"}>
                <Grid item xs={12} sm={6} md={6}>
                  <span style={{ fontWeight: "600" }}>Emergency :</span>
                  <span
                    style={{
                      marginLeft: "5px",
                      color: "#e03131",
                      fontWeight: 700,
                    }}
                  >
                    YES
                  </span>
                </Grid>
              </Grid>
            )}
            {showIssueData?.machineName && (
              <Grid container item xs={12} alignItems={"center"}>
                <Grid item xs={12} sm={6} md={6}>
                  <span style={{ fontWeight: "600" }}>Machine Name :</span>
                  <span style={{ marginLeft: "5px" }}>
                    {showIssueData?.machineName}
                  </span>
                </Grid>
              </Grid>
            )}
            {showIssueData?.reason && (
              <Grid container item xs={12} alignItems={"center"}>
                <Grid item xs={12} sm={6} md={6}>
                  <span style={{ fontWeight: "600" }}>Reason :</span>
                  <span style={{ marginLeft: "5px" }}>
                    {showIssueData?.reason}
                  </span>
                </Grid>
              </Grid>
            )}
            <Grid container item xs={12} alignItems={"center"}>
              {" "}
              <Grid item xs={12} sm={6} md={6}>
                <span style={{ fontWeight: "600" }}> Hall No : </span>
                <span style={{ marginLeft: "5px" }}>
                  {" "}
                  {showIssueData?.hall}{" "}
                </span>
              </Grid>
              {isSeek && !isTelevision && showSeekDateSelect && (
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  alignItems={"center"}
                >
                  <Grid
                    sx={{
                      "@media (max-width: 600px)": {
                        marginTop: "20px",
                      },
                    }}
                    item
                    xs={12}
                    sm={3.5}
                    md={2}
                  >
                    <span style={{ fontWeight: "600" }}> Seek Date : </span>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                    style={{ marginTop: "10px" }}
                  >
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="en-gb"
                    >
                      <DatePicker
                        label="Date"
                        value={selectedDate}
                        onChange={(newValue) => setSelectedDate(newValue)}
                        inputFormat="DD/MM/YYYY"
                        shouldDisableDate={(date) =>
                          date.isBefore(dayjs(), "day")
                        }
                        sx={{
                          "@media (max-width: 600px)": {
                            width: "100%",
                          },
                        }}
                        fullWidth
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>

                </Grid>
              )}
              {showSeekedDate && (
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  alignItems={"center"}
                >
                  <Grid
                    sx={{
                      "@media (max-width: 600px)": {
                        marginTop: "20px",
                      },
                    }}
                    item
                    xs={12}
                    sm={12}
                    md={6}
                  >
                    <span style={{ fontWeight: "600" }}>Seeked Date :</span>
                    <span style={{ marginLeft: "5px" }}>
                      {dayjs(isManagerResponse[0]?.seekedDate).format(
                        "DD-MM-YYYY"
                      )}
                    </span>
                  </Grid>
                  {isManagerResponse[0]?.seekReason && (
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      style={{ marginTop: "5px" }}
                    >
                      <span style={{ fontWeight: "600" }}>Seek Reason :</span>
                      <span style={{ marginLeft: "5px" }}>
                        {isManagerResponse[0]?.seekReason}
                      </span>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid container item xs={12} rowGap={3}>
            <Grid item xs={12}>
              <span style={{ fontWeight: "600" }}>Description of issue</span>
            </Grid>
            <Grid item xs={12}>
              <TextField
                multiline
                rows={4}
                value={showIssueData?.description}
                disabled={isManager}
                variant="outlined"
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container item xs={12}>
            <Grid container item xs={12} sm={12} md={12} rowGap={3}>
              <Grid
                container
                item
                xs={12}
                rowGap={2}
                alignContent={"flex-start"}
              >
                <Grid item xs={12}>
                  <span style={{ fontWeight: "600" }}>Issue image</span>
                </Grid>
                {showIssueData?.image?.length > 0 ? (
                  // Side by side with a fixed gap; grid columns (25-50% of
                  // the page) left wide blanks next to the 200px images
                  <Grid
                    item
                    xs={12}
                    style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}
                  >
                    {showIssueData?.image?.map((imgUrl, index) => (
                      <img
                        key={index}
                        src={imgUrl}
                        alt="Issued"
                        style={{
                          height: "200px",
                          width: "200px",
                          objectFit: "cover",
                          cursor: "pointer",
                        }}
                        onClick={() => handleClickOpen(imgUrl)}
                      />
                    ))}
                  </Grid>
                ) : (
                  <p>No image preview</p>
                )}
              </Grid>

              {/* <Grid container item xs={12} sm={12} md={12} rowGap={3}>
                <Grid
                  container
                  item
                  xs={12}
                  sm={3}
                  mb={2}
                  md={3}
                  rowGap={2}
                  alignContent={"flex-start"}
                >
                  <Grid item xs={12}>
                    <span style={{ fontWeight: "600" }}>Issue video</span>
                  </Grid>
                  {showIssueData?.video?.length > 0 ? (
                    <Grid item xs={12}>
                      <video
                        src={showIssueData?.video}
                        alt="Issued"
                        controls
                        style={{
                          height: "200px",
                          width: "200px",
                        }}
                      />
                    </Grid>
                  ) : (
                    <p>No video preview</p>
                  )}
                </Grid>
              </Grid> */}

              {isSeek && !isTelevision && showSeekDateSelect && (
                <Grid
                  container
                  item
                  xs={12}
                  sm={12}
                  rowGap={2}
                  mb={2}
                  alignContent={"flex-start"}
                >
                  <Grid
                    sx={{
                      "@media (max-width: 600px)": {
                        marginTop: "20px",
                      },
                    }}
                    item
                    xs={12}
                  >
                    <span style={{ fontWeight: "600" }}>Reason for Seek</span>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      multiline
                      rows={4}
                      value={seekReason}
                      variant="outlined"
                      fullWidth
                      onChange={(e) => setSeekReason(e.target.value)}
                    />
                  </Grid>
                </Grid>
              )}

              {isResolve && !isTelevision && (
                <>
                  {declinedSolution && (
                    <Grid
                      container
                      item
                      xs={12}
                      mb={2}
                      rowGap={1.5}
                      style={{
                        border: `1px solid ${STATUS_COLORS.REJECTED}`,
                        backgroundColor: "#fff5f5",
                        borderRadius: "6px",
                        padding: "16px",
                      }}
                    >
                      <Grid item xs={12}>
                        <span
                          style={{
                            fontWeight: 700,
                            color: STATUS_COLORS.REJECTED,
                          }}
                        >
                          Rejected solution
                        </span>
                        <span style={{ color: "#6b7280", marginLeft: "8px" }}>
                          (add a new solution below)
                        </span>
                      </Grid>
                      {lastDecline && (
                        <Grid
                          item
                          xs={12}
                          style={{
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <b>Reason :</b> {lastDecline.remarks}{" "}
                          <span style={{ color: "#6b7280" }}>
                            ({dayjs(lastDecline.date).format(
                              "DD-MM-YYYY, hh:mm A"
                            )}
                            {lastDecline.by ? `, ${lastDecline.by}` : ""})
                          </span>
                        </Grid>
                      )}
                      <Grid
                        item
                        xs={12}
                        style={{
                          wordBreak: "break-word",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <b>Solution :</b> {declinedSolution.solution || "NA"}
                      </Grid>
                      {solutionImages(declinedSolution).length > 0 && (
                        <Grid container item xs={12} gap={1}>
                          {solutionImages(declinedSolution).map((src, i) => (
                            <img
                              key={i}
                              src={src}
                              alt={`rejected solution ${i + 1}`}
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                                cursor: "pointer",
                              }}
                              onClick={() => handleClickOpen(src)}
                            />
                          ))}
                        </Grid>
                      )}
                    </Grid>
                  )}

                  <Grid
                    container
                    item
                    xs={12}
                    sm={6}
                    md={6}
                    mb={2}
                    rowGap={2}
                    alignContent={"flex-start"}
                  >
                    <Grid item xs={12}>
                      <span style={{ fontWeight: "600" }}>
                        Resolve images <span style={{ color: "red" }}>*</span>{" "}
                        <span style={{ fontWeight: 400, color: "#6b7280" }}>
                          (up to {MAX_TASK_SHEET_IMAGES})
                        </span>
                      </span>
                    </Grid>
                    {selectedFiles.length > 0 && (
                      <Grid container item xs={12} gap={2}>
                        {selectedFiles.map((file, index) => (
                          <Grid
                            item
                            key={index}
                            style={{ position: "relative" }}
                          >
                            <img
                              src={file}
                              alt={`Resolve ${index + 1}`}
                              style={{
                                height: "140px",
                                width: "140px",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                            />
                            <CloseIcon
                              aria-label={`Remove image ${index + 1}`}
                              style={{
                                cursor: "pointer",
                                position: "absolute",
                                top: 4,
                                right: 4,
                                background: "rgba(255,255,255,0.85)",
                                borderRadius: "50%",
                              }}
                              onClick={() =>
                                setSelectedFiles((prev) =>
                                  prev.filter((_, i) => i !== index)
                                )
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                    {selectedFiles.length < MAX_TASK_SHEET_IMAGES && (
                      <Grid item xs={12} sm={6} md={6}>
                        <Button
                          variant="contained"
                          style={{ textTransform: "none" }}
                          onClick={handleOpenDialog}
                          fullWidth
                        >
                          {selectedFiles.length
                            ? `Add image (${selectedFiles.length}/${MAX_TASK_SHEET_IMAGES})`
                            : "Upload"}
                        </Button>
                        {!selectedFiles.length && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "12px",
                              margin: "4px 0 0",
                            }}
                          >
                            Image is required to resolve
                          </p>
                        )}
                      </Grid>
                    )}
                  </Grid>
                  <Grid container item xs={12} sm={12} md={6}>
                    {isResolve && !isTelevision && (
                      <>
                        <Grid
                          container
                          item
                          xs={12}
                          sm={12}
                          rowGap={2}
                          mb={2}
                          alignContent={"flex-start"}
                        >
                          <Grid
                            sx={{
                              "@media (max-width: 600px)": {
                                marginTop: "20px",
                              },
                            }}
                            item
                            xs={12}
                          >
                            <span style={{ fontWeight: "600" }}>Solution</span>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              multiline
                              rows={4}
                              value={solution}
                              variant="outlined"
                              fullWidth
                              onChange={(e) => setSolution(e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <span style={{ fontWeight: "600" }}>
                              Employee Name{" "}
                              <span style={{ color: "red" }}>*</span>
                            </span>
                          </Grid>
                          <Grid item xs={12}>
                            <Autocomplete
                              fullWidth
                              options={employees}
                              value={employee}
                              onChange={(e, value) => setEmployee(value)}
                              loading={isEmployeesLoading}
                              getOptionLabel={(option) =>
                                `${option.name} (${option.tno || option.emplNo})`
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.emplNo === value.emplNo
                              }
                              renderOption={(props, option) => (
                                <li {...props} key={option.emplNo}>
                                  <ListItemText
                                    primary={option.name}
                                    secondary={`Emp No: ${
                                      option.tno || option.emplNo
                                    }`}
                                  />
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Search Employee"
                                  placeholder="Name or Emp No"
                                  error={isEmployeesError}
                                  helperText={
                                    isEmployeesError
                                      ? "Unable to load employees"
                                      : !employee &&
                                        "Employee name is required to resolve"
                                  }
                                  FormHelperTextProps={{
                                    sx: { color: "red", marginLeft: 0 },
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>

          <Grid container item xs={12} justifyContent={"flex-end"}>
            <Button
              variant="contained"
              color="primary"
              style={{
                // width: "200px",
                width: window.innerWidth <= 600 ? "100%" : "200px",
                textTransform: "none",
                background: `${isResolve && "#28A745"}`,
              }}
              disabled={
                isReportUpdateLoading ||
                (isSeek
                  ? selectedDate === null || seekReason.trim() === ""
                  : solution === "" || !selectedFile || !employee)
              }
              onClick={() => handleSeekOrResolve()}
            >
              {isReportUpdateLoading ? (
                <CircularProgress size={25} />
              ) : isManager && isSeek ? (
                "Seek"
              ) : (
                isManager && isResolve && "Resolve"
              )}
            </Button>
          </Grid>
        </>
      )}

      {/**this is for employee or sub manager raise report */}
      {isRaiseReport && (
        <>
          <Grid container item xs={12} rowGap={3}>
            <Grid
              container
              item
              xs={12}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Grid container item xs={12} sm={6} md={6} alignItems={"center"}>
                <Grid
                  container
                  style={{ fontWeight: "600" }}
                  item
                  xs={12}
                  sm={3}
                  md={3.2}
                >
                  Department :
                </Grid>
                <Grid container item xs={12} sm={6} md={6}>
                  <FormControl
                    sx={{
                      width: "200px",
                      marginLeft: "30px",
                      "@media (max-width: 600px)": {
                        width: "100%",
                        marginLeft: "0",
                        marginTop: "20px",
                      },
                    }}
                  >
                    <InputLabel id="select-department">
                      Select Department
                    </InputLabel>
                    <Select
                      labelId="select-department"
                      label="Select Department"
                      multiple
                      value={departments}
                      onChange={(e) => setDepartments(e.target.value)}
                      variant="outlined"
                      renderValue={(selected) => selected.join(", ")}
                    >
                      {DEPARTMENTS_TASKSHEETS?.map((dept) => (
                        <MenuItem key={dept} value={dept}>
                          <Checkbox checked={departments?.indexOf(dept) > -1} />
                          <ListItemText primary={dept} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid
                container
                sx={{
                  "@media (max-width: 600px)": {
                    marginTop: "20px",
                  },
                }}
                item
                xs={12}
                sm={6}
                md={6}
                alignItems={"center"}
              >
                <span style={{ fontWeight: "600" }}> Date :</span>{" "}
                <p style={{ marginLeft: "20px" }}>
                  {today.format("DD-MM-YYYY")}
                </p>
              </Grid>
            </Grid>

            <Grid
              container
              item
              xs={12}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              {" "}
              <Grid container item xs={12} sm={6} md={6} alignItems={"center"}>
                <Grid
                  style={{ fontWeight: "600" }}
                  container
                  item
                  xs={12}
                  sm={3}
                  md={3.2}
                >
                  Reporting Manager :{" "}
                </Grid>
                <Grid container item xs={12} sm={6} md={6}>
                  <FormControl
                    sx={{
                      width: "200px",
                      marginLeft: "30px",
                      "@media (max-width: 600px)": {
                        width: "100%",
                        marginLeft: "0",
                        marginTop: "20px",
                      },
                    }}
                  >
                    <InputLabel id="select-manager">Select Manager</InputLabel>
                    <Select
                      labelId="select-manager"
                      label="Select Manager"
                      multiple
                      value={reportingManager?.map((manager) => manager.id)}
                      onChange={handleManagerChange}
                      variant="outlined"
                      renderValue={(selected) =>
                        selected
                          .map((id) => {
                            const manager = filteredManagers?.find(
                              (mgr) => mgr.id === id
                            );
                            return manager ? manager.name : "";
                          })
                          .join(", ")
                      }
                    >
                      {/* {filteredManagers?.length > 0 &&
                        filteredManagers?.map((manager) => (
                          <MenuItem
                            key={manager.id}
                            value={manager.id}
                            name={manager.name}
                          >
                            <Checkbox
                              checked={
                                reportingManager?.indexOf(manager.id) > -1
                              }
                            />
                            <ListItemText primary={manager.name} />
                          </MenuItem>
                        ))} */}

                      {filteredManagers?.length > 0 &&
                        filteredManagers?.map((manager) => (
                          <MenuItem
                            key={manager.id}
                            value={manager.id}
                            onClick={() => toggleManagerSelection(manager)}
                          >
                            <Checkbox
                              checked={reportingManager?.some(
                                (mgr) => mgr.id === manager.id
                              )}
                            />
                            <ListItemText primary={manager.name} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container item xs={12} sm={6} md={6} alignItems={"center"}>
                <Grid
                  sx={{
                    fontWeight: "600",
                    "@media (max-width: 600px)": {
                      marginTop: "20px",
                    },
                  }}
                  container
                  item
                  xs={12}
                  sm={1.5}
                  md={1.5}
                >
                  Hall No :{" "}
                </Grid>
                <Grid container item xs={12} sm={6} md={6}>
                  <FormControl
                    sx={{
                      width: "200px",
                      "@media (max-width: 600px)": {
                        width: "100%",
                        marginTop: "20px",
                      },
                    }}
                  >
                    <InputLabel id="select-hall">Select Hall no</InputLabel>
                    <Select
                      labelId="select-hall"
                      label="Select Hall no"
                      value={hallNo}
                      onChange={(e) => setHallNo(e.target.value)}
                      variant="outlined"
                    >
                      {HALL_NO?.map((hall) => (
                        <MenuItem key={hall} value={hall}>
                          {hall}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid container item xs={12} alignItems={"center"}>
              <Grid container item xs={12} sm={6} md={6} alignItems={"center"}>
                <Grid
                  style={{ fontWeight: "600" }}
                  container
                  item
                  xs={12}
                  sm={3}
                  md={3.2}
                >
                  Machine Name <span style={{ color: "red" }}>&nbsp;*</span>
                  &nbsp;:
                </Grid>
                <Grid container item xs={12} sm={8} md={8}>
                  <Autocomplete
                    sx={{
                      width: "320px",
                      marginLeft: "30px",
                      "@media (max-width: 600px)": {
                        width: "100%",
                        marginLeft: "0",
                        marginTop: "20px",
                      },
                    }}
                    options={machines}
                    value={machine}
                    onChange={(e, value) => setMachine(value)}
                    loading={isMachinesLoading}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.mcCode === value.mcCode
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.mcCode}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Machine"
                        placeholder="Machine name"
                        error={isMachinesError}
                        helperText={
                          isMachinesError && "Unable to load machines"
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid container item xs={12} alignItems={"center"}>
              <Grid container item xs={12} sm={6} md={6} alignItems={"center"}>
                <Grid
                  style={{ fontWeight: "600" }}
                  container
                  item
                  xs={12}
                  sm={3}
                  md={3.2}
                >
                  Reason
                  {REASON_REQUIRED && (
                    <span style={{ color: "red" }}>&nbsp;*</span>
                  )}
                  &nbsp;:
                </Grid>
                <Grid container item xs={12} sm={8} md={8}>
                  <Autocomplete
                    sx={{
                      width: "320px",
                      marginLeft: "30px",
                      "@media (max-width: 600px)": {
                        width: "100%",
                        marginLeft: "0",
                        marginTop: "20px",
                      },
                    }}
                    value={reason}
                    options={reasons}
                    onChange={handleReasonChange}
                    loading={isReasonsLoading || isAddingReason}
                    disabled={isAddingReason}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    filterOptions={(options, params) => {
                      const filtered = filterReasons(options, params);
                      if (!canAddReasons) return filtered;
                      const typed = normalizeReason(params.inputValue);
                      const exists = options.some(
                        (option) =>
                          option.name.toLowerCase() === typed.toLowerCase()
                      );
                      // Permitted logins: offer to add what was typed
                      if (typed.length >= 2 && !exists) {
                        filtered.push({
                          isNew: true,
                          inputValue: typed,
                          name: `Add "${typed}"`,
                        });
                      }
                      return filtered;
                    }}
                    getOptionLabel={(option) =>
                      option.isNew ? option.inputValue : option.name
                    }
                    // By name: configured default reasons have no id
                    isOptionEqualToValue={(option, value) =>
                      option.name === value.name
                    }
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        key={
                          option.isNew
                            ? `new-${option.inputValue}`
                            : option.id ?? `default-${option.name}`
                        }
                        style={
                          option.isNew
                            ? { fontWeight: 600, color: "#1976d2" }
                            : undefined
                        }
                      >
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          canAddReasons ? "Select or add a reason" : "Select a reason"
                        }
                        placeholder={
                          canAddReasons ? "Type to search or add" : "Type to search"
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid container item xs={12} alignItems={"center"}>
              <Grid container item xs={12} sm={6} md={6} alignItems={"center"}>
                <Grid
                  style={{ fontWeight: "600" }}
                  container
                  item
                  xs={4}
                  sm={3}
                  md={3.2}
                >
                  Emergency :
                </Grid>
                <Grid item>
                  <Checkbox
                    checked={isEmergency}
                    onChange={(e) => setIsEmergency(e.target.checked)}
                    inputProps={{ "aria-label": "Emergency" }}
                    sx={{
                      marginLeft: "18px",
                      color: "#e03131",
                      "&.Mui-checked": { color: "#e03131" },
                      "@media (max-width: 600px)": { marginLeft: 0 },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container item xs={12} rowGap={2}>
            <Grid item style={{ fontWeight: "600" }} xs={12}>
              Description of issue :
            </Grid>
            <Grid item xs={12}>
              <TextField
                multiline
                rows={4}
                value={issue}
                variant="outlined"
                fullWidth
                onChange={(e) => setIssue(e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container item xs={12} alignContent={"flex-start"}>
            <Grid mt={4} container item xs={12} sm={12} md={12} rowGap={2}>
              <Grid style={{ fontWeight: "600" }} item xs={12}>
                Upload Image{" "}
                <span style={{ fontWeight: 400, color: "#6b7280" }}>
                  (up to {MAX_TASK_SHEET_IMAGES})
                </span>
              </Grid>
              <Grid container item xs={12} md={12} gap={2}>
                {selectedFilesMulti?.length > 0 ? (
                  selectedFilesMulti?.map((file, index) => (
                    <Grid item key={index} style={{ position: "relative" }}>
                      <img
                        src={file}
                        alt={`Uploaded ${index + 1}`}
                        style={{
                          height: "160px",
                          width: "160px",
                          objectFit: "cover",
                          borderRadius: "4px",
                          display: "block",
                        }}
                      />
                      <CloseIcon
                        aria-label={`Remove image ${index + 1}`}
                        style={{
                          cursor: "pointer",
                          position: "absolute",
                          top: 4,
                          right: 4,
                          background: "rgba(255,255,255,0.85)",
                          borderRadius: "50%",
                        }}
                        onClick={() => handleRemoveImage(index)}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="contained"
                      style={{ textTransform: "none" }}
                      onClick={handleOpenDialog}
                      fullWidth
                      sx={{ height: "40px" }}
                    >
                      Upload
                    </Button>
                  </Grid>
                )}
              </Grid>
              {selectedFilesMulti.length > 0 &&
                selectedFilesMulti.length < MAX_TASK_SHEET_IMAGES && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="contained"
                      style={{ textTransform: "none", marginTop: "10px" }}
                      onClick={handleOpenDialog}
                      fullWidth
                      sx={{ height: "40px" }}
                    >
                      Add More Images ({selectedFilesMulti.length}/
                      {MAX_TASK_SHEET_IMAGES})
                    </Button>
                  </Grid>
                )}
              {selectedFilesMulti.length >= MAX_TASK_SHEET_IMAGES && (
                <Grid item xs={12}>
                  <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>
                    Maximum of {MAX_TASK_SHEET_IMAGES} images reached. Remove
                    one to add another.
                  </p>
                </Grid>
              )}
            </Grid>

            {/* <Grid mt={4} container item xs={12} sm={6.1} md={6} rowGap={2}>
              <Grid style={{ fontWeight: "600" }} item xs={12}>
                Upload video
              </Grid>

              <Grid container item xs={12} md={6}>
                {selectedVideo ? (
                  <>
                    {" "}
                    <Grid item style={{ position: "relative" }}>
                      <video
                        src={selectedVideo}
                        alt="Uploaded"
                        controls
                        style={{
                          height: "200px",
                          width: "200px",
                        }}
                      />
                      <Grid item>
                        <CloseIcon
                          style={{
                            cursor: "pointer",
                            position: "absolute",
                            top: "7%",
                            right: "-5%",
                          }}
                          onClick={() => {
                            setSelectedVideo(null);
                            setUrlFile(null);
                            setVideoRecordUrl(null);
                            setVideoRecordFile(null);
                          }}
                        />
                      </Grid>
                    </Grid>{" "}
                  </>
                ) : (
                  <Button
                    variant="contained"
                    style={{ textTransform: "none" }}
                    onClick={handleOpenDialogVideo}
                    fullWidth
                    sx={{ height: "40px" }}
                  >
                    Upload
                  </Button>
                )}
              </Grid>
            </Grid> */}
          </Grid>

          <Grid container item xs={12} justifyContent={"flex-end"}>
            <Button
              variant="contained"
              color="primary"
              style={{
                width: window.innerWidth <= 600 ? "100%" : "200px",
                textTransform: "none",
              }}
              disabled={isReportCreateLoading || !checkForAllFields}
              onClick={handleSubmitReportIssue}
            >
              {isReportCreateLoading ? (
                <CircularProgress size={25} />
              ) : (
                "Report issue"
              )}
            </Button>
          </Grid>
        </>
      )}

      {/**this dialog box for showing the image to the manager */}
      <Dialog open={openNew} onClose={handleClose} maxWidth="lg">
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="custom-scrollbar">
          <Grid>
            <img
              src={selectedImage}
              alt="issue"
              style={{
                height: "100%",
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Grid>
        </DialogContent>
      </Dialog>

      {/**this dialog box for select the image when raise an report */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          <Grid container item xs={12}>
            <Grid container item xs={6}>
              {" "}
              Upload Image{" "}
            </Grid>
            <Grid container item xs={6} justifyContent={"flex-end"}>
              {" "}
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                color="primary"
                style={{
                  textTransform: "none",
                  background: "#e03131",
                  color: "#FFF",
                }}
                // startIcon={<CloseIcon />}
              >
                <CloseIcon />
              </Button>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          {!openCamera ? (
            <>
              <Button
                variant="contained"
                component="label"
                style={{ textTransform: "none" }}
                fullWidth
              >
                Select Image File
                {isResolve && !isTelevision && (
                  <input
                    type="file"
                    multiple
                    hidden
                    accept="image/jpeg, image/png"
                    onChange={handleFileChange}
                  />
                )}
                {isRaiseReport && (
                  <input
                    type="file"
                    multiple
                    hidden
                    accept="image/jpeg, image/png"
                    onChange={handleFileChange}
                  />
                )}
              </Button>
              <Button
                variant="contained"
                fullWidth
                style={{ marginTop: "20px", textTransform: "none" }}
                onClick={handleCameraClick}
              >
                Open Camera
              </Button>
            </>
          ) : (
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width="100%"
              videoConstraints={{
                facingMode: facingMode,
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {openCamera && (
            <>
              <Button
                onClick={capture}
                variant="contained"
                color="primary"
                style={{ textTransform: "none" }}
                startIcon={<LinkedCameraIcon />}
              >
                Capture
              </Button>

              <Button
                onClick={handleToggleCamera}
                variant="contained"
                color="primary"
                style={{ textTransform: "none" }}
                startIcon={<CameraswitchIcon />}
              >
                Switch Camera
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={openDialogVideo} onClose={handleCloseDialogVideo}>
        <DialogTitle>
          <Grid container item xs={12}>
            <Grid container item xs={6}>
              Upload Video
            </Grid>
            <Grid container item xs={6} justifyContent="flex-end">
              <Button
                onClick={handleCloseDialogVideo}
                variant="outlined"
                color="primary"
                style={{
                  textTransform: "none",
                  background: "#e03131",
                  color: "#FFF",
                }}
              >
                <CloseIcon />
              </Button>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          {!openVideoCamera ? (
            <>
              <Button
                variant="contained"
                component="label"
                style={{ textTransform: "none" }}
                fullWidth
              >
                Select Video File
                <input
                  type="file"
                  hidden
                  accept=".mp4"
                  onChange={handleVideoFileChange}
                />
              </Button>
              <Button
                variant="contained"
                fullWidth
                style={{ marginTop: "20px", textTransform: "none" }}
                onClick={handleCameraVideoClick}
              >
                Open Camera
              </Button>
            </>
          ) : (
            <>
              <Webcam
                audio={false}
                ref={webcamRefVideo}
                screenshotFormat="image/jpeg"
                width="100%"
                videoConstraints={{
                  facingMode: facingMode,
                }}
              />

              <Grid sx={{ position: "absolute", bottom: 80, left: 30 }}>
                <video ref={videoRef} autoPlay playsInline muted />
                {isRecording && (
                  <div
                    style={{
                      width: "40px",
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "#fff",
                      padding: "5px 10px",
                      borderRadius: "5px",
                    }}
                  >
                    {formatTime(recordingTime)}
                  </div>
                )}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {openVideoCamera && (
            <Button
              onClick={handleStartStopRecording}
              variant="contained"
              color="primary"
              style={{ textTransform: "none" }}
            >
              {isRecording ? <StopCircle /> : <PlayCircleOutline />}
              {isRecording ? "Stop Record" : "Start Record"}
            </Button>
          )}

          {openVideoCamera && !isRecording && (
            <Button
              onClick={captureVideo}
              variant="contained"
              color="primary"
              disabled={!videoRecordUrl}
              style={{ textTransform: "none" }}
              startIcon={<FileUpload />}
            >
              Upload Video
            </Button>
          )}
          {openVideoCamera && (
            <Button
              onClick={handleToggleCamera}
              variant="contained"
              color="primary"
              style={{ textTransform: "none" }}
              startIcon={<CameraswitchIcon />}
            >
              Switch Camera
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={loadingVideo}>
        <DialogContent>
          <Grid sx={{ color: "red" }}>Wait for a minute</Grid>
        </DialogContent>
      </Dialog>
    </Grid>
  );
}

export default TaskSheetReportAndResolve;
